<template>
  <div class="confirm pt-30">
    <v-row style="margin-top: 100px">
      <v-col
        lg="4" offset-lg="4"
        md="6" offset-md="3"
        sm="8" offset-sm="2"
        xs="12"
      >
        <v-card
          class="mx-auto"
          outlined
          elevation="1"
        >
          <v-card-title style="background-color: #D4EBE5; color:#02AB98; padding: 0">
            <h5 class="center" >認証コードを入力</h5>
          </v-card-title>
          <v-card-text class="pa-5">
            <validation-observer ref="observer">
              <v-alert
                border="left"
                colored-border
                type="info"
                elevation="2"
                style="font-size: 90%"
              >
                ご登録いただいたメールアドレスへ認証コードを送信しました。<br>
                認証コード入力後、ログイン画面に遷移しますので再度ログインしてください。<br/>
                <p style="text-indent: -1em;" class="mt-3 pl-4">
                  <b>※認証コードが届かない場合</b><br/>
                  メールアドレスが間違っているか、受信制限されている可能性があります。<br>
                  ログイン画面に戻り、ユーザー登録をし直してください。<br>
                  受信制限されている方は、「@kenchare.net」ドメインを受信できるように設定をお願い致します。<br/>
                  それでも届かない場合、ログイン画面下部のサポートデスクまでお問い合わせください。<br/><br/>
                  <b>メールサーバにより、認証コード受信までに時差が発生することがあります。</b><br/>
                  なお、コードを再送信した場合は、最後に受信した認証コードを入力いただく必要がありますので、ご注意ください。
                </p>
              </v-alert>
              <v-alert
                v-if="alert"
                border="left"
                colored-border
                type="error"
                elevation="2"
                style="font-size: 90%"
              >
                {{ error }}
              </v-alert>
              <v-form @submit.prevent="submit" ref="form">
                <v-row dense>
                  <v-col cols="12">
                    <validation-provider
                      name="認証コード"
                      rules="required|confirmCode"
                      v-slot="{ errors }"
                    >
                      <v-text-field
                        v-model="signUpConfirm.code"
                        :error-messages="errors"
                        label="認証コード"
                        type="tel"
                        outlined
                        dense
                      ></v-text-field>
                    </validation-provider>
                  </v-col>
                </v-row>
                <div class="center">
                  <v-btn
                    type="submit"
                    color="primary"
                    class="mt-8"
                    :loading="isLoading"
                  >
                    送信
                  </v-btn>
                </div>
              </v-form>
            </validation-observer>
            <div class='authlinks mt-10 text-left'>
              <v-btn text color="primary" @click="signIn">
                ログイン画面に戻る
              </v-btn>
            </div>
            <div class='authlinks flex-left text-left'>
              <v-btn text color="primary" @click="resend">
                コード再送信
              </v-btn>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
    <v-snackbar
      v-model="isResendAlert"
      :timeout="3000"
      color="primary"
      top
    >
      認証コードを再送信しました。
      <template v-slot:action="{ attrs }">
        <v-btn
          icon
          color="white"
          v-bind="attrs"
          @click="isResendAlert = false"
        >
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import Vue from 'vue'
import AmplifyEventBus from './AmplifyEventBus'
import { Auth, I18n } from 'aws-amplify'
import { translate } from '@/i18n'
import { required } from 'vee-validate/dist/rules'
import { localize, extend, ValidationObserver, ValidationProvider, setInteractionMode } from 'vee-validate'
import ja from 'vee-validate/dist/locale/ja.json'

setInteractionMode('eager')
localize('ja', ja)
extend('required', required)
extend('confirmCode', (value) => {
  const inputCode = value.replace(/[０-９]/g, function (s) {
    return String.fromCharCode(s.charCodeAt(0) - 0xFEE0)
  })
  return /^[0-9０－９]{6}$/.test(inputCode) || '{_field_} は6桁の数字で入力してください'
})

export default Vue.extend({
  name: 'HCConfirmSignUp',
  props: ['confirmSignUpConfig'],
  components: {
    ValidationProvider,
    ValidationObserver
  },
  data () {
    return {
      pageType: 'confirm',
      signUpConfirm: {
        code: ''
      },
      isLoading: false,
      isResendAlert: false,
      error: '',
      alert: false
    }
  },
  computed: {
    options () {
      const defaults = {
        username: '',
        header: '認証コードを入力'
      }
      return Object.assign(defaults, this.confirmSignUpConfig || {})
    }
  },
  mounted: function () {
  },
  methods: {
    async submit (e) {
      this.alert = false
      this.isLoading = true
      const isValidateError = await this.$refs.observer.validate()
      if (!isValidateError) {
        this.alert = true
        this.error = '入力内容にエラーがあります。確認してください。'
        this.isLoading = false
        return false
      }
      const inputCode = this.signUpConfirm.code.replace(/[０-９]/g, function (s) {
        return String.fromCharCode(s.charCodeAt(0) - 0xFEE0)
      })
      Auth.confirmSignUp(this.options.username, inputCode)
        .then(async (data) => {
          this.$log.info('confirmSignUp success')
          this.$emit('signUpSuccessAlert')
          AmplifyEventBus.$emit('authState', 'signIn')
        })
        .catch((e) => {
          this.setError(e)
        })
    },
    resend () {
      this.alert = false
      this.isResendAlert = true
      this.$refs.observer.reset()
      this.errors = null
      this.signUpConfirm.code = ''
      Auth.resendSignUp(this.options.username)
        .then(() => {
          this.$log.info('resendSignUp success')
        })
        .catch((e) => {
          this.setError(e)
        })
    },
    signIn () {
      AmplifyEventBus.$emit('authState', 'signIn')
    },
    setError (e) {
      this.isLoading = false
      const errorMessage = I18n.get(e.message || e)
      this.$log.error(errorMessage)
      if (errorMessage === 'Invalid verification code provided, please try again.') {
        this.error = `${translate('auth.errors.invalidVerificationCode')}`
      } else if (errorMessage === 'Attempt limit exceeded, please try after some time.') {
        this.error = `${translate('auth.errors.limitExceededException')}`
      } else {
        this.error = `${translate('auth.errors.unexpectedError')}`
      }
      this.alert = true
    }
  }
})
</script>
<style scoped lang="scss">
.confirm {
  .center {
    margin: 5px auto;
    text-align: center;
  }
}
.authlinks {
  display: block;
  margin-top:10px;
}
</style>
